/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The three button shapes of a detail screen's status band.
 *
 * `Button`'s own variants cannot express these. Its sizes are height-driven
 * (`h-9 px-4`) where the design is padding-driven, and its `outlined` variant
 * is a filled beige control with a brand-coloured border — louder than the
 * neutral outline a secondary action wants, and loud enough that a row of
 * them competes with the one solid primary. All three are used with
 * `size="custom" variant="none"`.
 *
 * They live here rather than in one feature because the deal, sale and lease
 * bands are the same control strip three times, and the first attempt at this
 * had each screen hand-roll its own — which is how the sale screen ended up
 * with a beige secondary next to the deal screen's white one.
 */

/** Solid seed. Exactly one per screen. */
export const DETAIL_ACTION_PRIMARY =
  'border border-transparent bg-(--seed-base) px-(--space-5) py-(--space-2-5) '
  + 'text-[length:var(--text-small)] [font-weight:var(--font-bold)] '
  + 'text-(--text-inverse) hover:opacity-90';

/**
 * Seed outline, transparent.
 *
 * It used to fill with `--surface-card` and outline in
 * `--border-medium`. Once the detail strips moved onto the page ground,
 * that fill matched the surface behind it exactly -- so the fill did
 * nothing and the button was a loose rectangle of border, in a grey a
 * shade darker and a shade cooler than every other hairline on the
 * screen. Transparent, on the same `--border-subtle` as the cards, it
 * reads as part of the strip instead of a patch laid on it.
 *
 * The label is the seed, like every other interactive thing on these
 * screens -- the links, the active tab, the headline figure. In grey it
 * was the only control that did not look like one.
 */
export const DETAIL_ACTION_SECONDARY =
  'border border-(--border-subtle) bg-transparent px-(--space-3-5) py-(--space-2-5) '
  + 'text-[length:var(--text-small)] [font-weight:var(--font-bold)] '
  + 'text-(--seed-base) hover:border-(--seed-base) hover:bg-(--surface-hover)';

/**
 * No border, no fill. For the actions that end things.
 *
 * Same horizontal padding as the secondary: three buttons in a row at
 * 16px, 12px and 12px read as three sizes rather than one set.
 */
export const DETAIL_ACTION_GHOST =
  'border border-transparent bg-transparent px-(--space-3-5) py-(--space-2-5) '
  + 'text-[length:var(--text-small)] [font-weight:var(--font-bold)] '
  + 'text-(--seed-base) hover:bg-(--surface-hover)';
